<template>
  <form class="flex flex-col gap-sm pb-sm" @submit="onSubmit">
    <FormGroup>
      <KontaktorsakInput
        v-model:kontaktorsakId="kontaktorsakId"
        v-model:fortydligadKontaktorsak="fortydligadKontaktorsak"
        :selectedSamtalsamneIds="values.samtalsamneIds || []"
        @push="pushSamtalsamne"
        @remove="removeSamtalsamne"
      />
    </FormGroup>
    <FormGroup>
      <InternaKommentarer
        v-model="internKommentar"
        :required="shouldRequireInternKommentar(values.lamnaOverTill)"
        :show-verified="false"
        :can-edit="false"
      />
    </FormGroup>
    <FormGroup>
      <KanalInput v-model="kanal" required :options="UnlinkedKanalOptions" />
      <KundgruppInput v-model="kundgrupp" :options="UnlinkedKundgruppOptions" />
      <OmbudInput v-model="ombud" />
    </FormGroup>
    <CollapsableFormGroup id="hantering" title="Hantering">
      <LamnaOverInput
        v-model="lamnaOverTill"
        :options="UnlinkedLamnaOverTillOptionGroups"
      />
      <EmailInput
        v-model="email"
        label="Kundens e-postadress"
        :required="shouldRequireEmail(lamnaOverTill)"
      />
      <ArendetypInput v-model="arendetyp" />
    </CollapsableFormGroup>
    <avtalat-message v-if="state.arendeCreated" type="positive" small
      ><p>Ett nytt ärende har skapats</p></avtalat-message
    >
    <div>
      <avtalat-message v-if="arendeStore.arende.error" type="negative" small
        ><p>
          {{
            arendeStore.arende.error.errors ??
            "Något har gått fel. Kontakta IT-drift."
          }}
        </p></avtalat-message
      >
    </div>
    <div class="gap-2 flex flex-row justify-end">
      <avtalat-button
        id="save-button"
        v-if="saveButtonIsVisible"
        outlined
        :disabled="!meta.valid"
        :loading="isSubmitting"
        @click="setArendeCloseStatus(false)"
        >Spara</avtalat-button
      >
      <avtalat-button
        id="save-close-button"
        v-if="saveCloseButtonIsVisible"
        :disabled="!meta.valid"
        :loading="isSubmitting"
        @click="setArendeCloseStatus(true)"
        >Spara och stäng</avtalat-button
      >
    </div>
  </form>
</template>
<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { configure, useField, useFieldArray, useForm } from "vee-validate";
import { computed, reactive } from "vue";
import * as z from "zod";

import {
  ArendetypInput,
  EmailInput,
  InternaKommentarer,
  KanalInput,
  KontaktorsakInput,
  KundgruppInput,
  LamnaOverInput,
  OmbudInput,
} from "@/components/form-components";
import CollapsableFormGroup from "@/components/ui/CollapsableFormGroup.vue";
import FormGroup from "@/components/ui/FormGroup.vue";
import {
  ArendeType,
  KanalType,
  KundgruppType,
  LamnaOverType,
} from "@/composables/KundserviceAppApiClient";
import { useApi } from "@/composables/useApi";
import { useNavigation } from "@/composables/useNavigation";
import { useConfigStore } from "@/stores";
import { InternalCommentErrorMessage } from "@/common/constants";
import {
  mapPuzzelQueryStringToKanalType,
  mapPuzzelQueryStringToKundgruppType,
} from "@/common/mappers";
import { useAnstalldarendeStore } from "@/views/anstalld/store/anstalldarendeStore";

import {
  UnlinkedKanalOptions,
  UnlinkedKundgruppOptions,
  UnlinkedLamnaOverTillOptionGroups,
} from "./formConstants";
import {
  shouldRequireEmail,
  shouldRequireInternKommentar,
  showSaveButton,
  showSaveCloseButton,
} from "./formRules";

configure({
  validateOnChange: true,
  validateOnInput: true,
  validateOnBlur: false,
});

const apiClient = useApi();
const navigation = useNavigation();
const configStore = useConfigStore();
const arendeStore = useAnstalldarendeStore();

const state = reactive({
  arendeCreated: false,
});

const zKontaktorsak = z.string().min(1, "En kontaktorsak måste väljas");
const zFortydligadKontaktorsak = z
  .string()
  .max(1000, "Får inte överskrida 1000 tecken")
  .nullable()
  .optional()
  .or(z.literal(""))
  .superRefine((value, ctx) => {
    if (
      kontaktorsakId.value === configStore.getKontaktorsakOvrigt?.id &&
      !value
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Förtydliga kontaktorsak",
      });
    }
  });
const zSamtalsamne = z.string();
const zSamtalsamnen = z
  .array(zSamtalsamne)
  .max(6, "Får inte överskrida 6 övriga samtalsämnen")
  .optional();
const zInternKommentar = z
  .string()
  .max(1000, "Får inte överskrida 1000 tecken")
  .optional()
  .superRefine((value, ctx) => {
    if (shouldRequireInternKommentar(values.lamnaOverTill) && !value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: InternalCommentErrorMessage(values.lamnaOverTill),
      });
    }
  });
const zEmail = z
  .string()
  .email({ message: "E-postadressen är inte giltig" })
  .optional()
  .or(z.literal(""))
  .superRefine((value, ctx) => {
    if (shouldRequireEmail(values.lamnaOverTill) && !value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "E-postadress måste anges vid överlämning till andra tjänster",
      });
    }
  });

const formSchema = toTypedSchema(
  z.object({
    kontaktorsakId: zKontaktorsak,
    fortydligadKontaktorsak: zFortydligadKontaktorsak,
    samtalsamneIds: zSamtalsamnen,
    internKommentar: zInternKommentar,
    kanal: z.nativeEnum(KanalType),
    kundgrupp: z.nativeEnum(KundgruppType),
    ombud: z.boolean().optional(),
    email: zEmail,
    arendetyp: z.nativeEnum(ArendeType),
    close: z.boolean().optional(),
    lamnaOverTill: z.nativeEnum(LamnaOverType),
  }),
);

const { meta, values, handleSubmit, isSubmitting, resetForm, validate } =
  useForm({
    validationSchema: formSchema,
    initialValues: {
      kontaktorsakId: "",
      fortydligadKontaktorsak: "",
      samtalsamneIds: [],
      internKommentar: "",
      kundgrupp:
        mapPuzzelQueryStringToKundgruppType(
          navigation.currentRoute.query.kundgrupp?.toString(),
        ) ?? KundgruppType.Tjansteman,
      ombud: false,
      arendetyp: ArendeType.Kundservicearende,
      hanteringstid: undefined,
      kanal: mapPuzzelQueryStringToKanalType(
        navigation.currentRoute.query.mediakanal?.toString(),
      ),
      close: false,
      lamnaOverTill: LamnaOverType.Nej,
      email: "",
    },
  });

const { value: kontaktorsakId } = useField<string>("kontaktorsakId");
const { value: fortydligadKontaktorsak } = useField<string>(
  "fortydligadKontaktorsak",
);
const { remove: removeSamtalsamne, push: pushSamtalsamne } =
  useFieldArray<string>("samtalsamneIds");
const { value: internKommentar } = useField<string>("internKommentar");
const { value: kanal } = useField<KanalType>("kanal");
const { value: kundgrupp } = useField<KundgruppType>("kundgrupp");
const { value: email } = useField<string>("email");
const { value: arendetyp } = useField<ArendeType>("arendetyp");
const { value: lamnaOverTill } = useField<LamnaOverType>("lamnaOverTill");
const { setValue: setArendeCloseStatus } = useField<boolean>("close");
const { value: ombud } = useField<boolean>("ombud");

const saveButtonIsVisible = computed(() =>
  showSaveButton(values.lamnaOverTill, false),
);
const saveCloseButtonIsVisible = computed(() =>
  showSaveCloseButton(values.lamnaOverTill),
);

const onSubmit = handleSubmit(async (values) => {
  const { valid } = await validate();

  if (!valid) {
    return;
  }

  const fortydligadKontaktorsak =
    values.kontaktorsakId === configStore.getKontaktorsakOvrigt?.id
      ? values.fortydligadKontaktorsak
      : "";
  let newArende;

  if (values.email) {
    newArende = await arendeStore.createArende(
      {
        puzzelId:
          navigation.currentRoute.query.externalpuzzelid?.toString() ??
          undefined,
        individId: "",
        email: values.email,
        telefonnummer: "",
        kanal: values.kanal!,
        kundgrupp: values.kundgrupp,
        arendetyp: values.arendetyp,
        internalNote: values.internKommentar,
        kontaktorsakId: values.kontaktorsakId,
        kontaktorsakNamn:
          configStore.getKontaktorsakById(values.kontaktorsakId)?.namn ?? "",
        fortydligadKontaktorsak: fortydligadKontaktorsak,
        samtalsamneIds: values.samtalsamneIds,
        conversation: undefined,
        isClosed: values.close,
        isVerified: false,
        isManuallyVerified: false,
        lamnaOverTill: values.lamnaOverTill,
        ombud: values.ombud,
      },
      apiClient,
    );
  } else {
    newArende = await arendeStore.createUnlinkedArende(
      {
        puzzelId:
          navigation.currentRoute.query.externalpuzzelid?.toString() ??
          undefined,
        kontaktorsakId: values.kontaktorsakId,
        kontaktorsakNamn:
          configStore.getKontaktorsakById(values.kontaktorsakId)?.namn ?? "",
        fortydligadKontaktorsak: values.fortydligadKontaktorsak,
        samtalsamneIds: values.samtalsamneIds,
        internalNote: values.internKommentar,
        isClosed: values.close,
        kundgrupp: values.kundgrupp,
        arendetyp: values.arendetyp,
        lamnaOverTill: values.lamnaOverTill,
        kanal: values.kanal!,
        ombud: values.ombud,
      },
      apiClient,
    );
  }

  if (newArende?.id) {
    resetForm();
    showArendeCreated();
  }
});

const showArendeCreated = () => {
  state.arendeCreated = true;
  setTimeout(() => {
    state.arendeCreated = false;
  }, 5000);
};
</script>
